import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainerReactTable from '../../../../../Components/Common/TableContainerReactTable';
import SelectInput from '../../common/SelectInput';
import FormInput from '../../common/FormInput';
import classnames from 'classnames';

export interface FinanceCategoryData {
  id: string;
  name: string;
  assetAccount: string;
  expenseAccount: string;
  incomeAccount: string;
}

export interface FinanceCategorySearchForm {
  searchField: string;
  searchValue: string;
}

interface FinanceCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (financeCategory: FinanceCategoryData) => void;
}

const defaultSearchForm: FinanceCategorySearchForm = {
  searchField: 'Name',
  searchValue: ''
};

// Mock finance category data - in real app this would come from API
const mockFinanceCategories: FinanceCategoryData[] = [
  {
    id: '1',
    name: 'Inventory',
    assetAccount: '1200 - Inventory Asset',
    expenseAccount: '5000 - Cost of Goods Sold',
    incomeAccount: '4000 - Sales Revenue'
  },
  {
    id: '2',
    name: 'Services',
    assetAccount: '1210 - Service Assets',
    expenseAccount: '5100 - Service Expenses',
    incomeAccount: '4100 - Service Revenue'
  },
  {
    id: '3',
    name: 'Equipment',
    assetAccount: '1500 - Equipment',
    expenseAccount: '5200 - Equipment Expenses',
    incomeAccount: '4200 - Equipment Revenue'
  },
  {
    id: '4',
    name: 'Software',
    assetAccount: '1300 - Software Assets',
    expenseAccount: '5300 - Software Expenses',
    incomeAccount: '4300 - Software Revenue'
  },
  {
    id: '5',
    name: 'Consulting',
    assetAccount: '1400 - Consulting Assets',
    expenseAccount: '5400 - Consulting Expenses',
    incomeAccount: '4400 - Consulting Revenue'
  }
];

const searchFieldOptions = ['Name', 'Asset Account', 'Expense Account', 'Income Account'];

const FinanceCategoryModal: React.FC<FinanceCategoryModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelect 
}) => {
  const [searchForm, setSearchForm] = useState<FinanceCategorySearchForm>(defaultSearchForm);
  const [financeCategories, setFinanceCategories] = useState<FinanceCategoryData[]>(mockFinanceCategories);
  const [selectedFinanceCategory, setSelectedFinanceCategory] = useState<FinanceCategoryData | null>(null);
  const [activeTab] = useState('1'); // Only one tab, but keep for extensibility

  const handleSearchFieldChange = (field: keyof FinanceCategorySearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    // Filter finance categories based on search criteria
    const filtered = mockFinanceCategories.filter(category => {
      let fieldValue = '';
      
      switch (searchForm.searchField) {
        case 'Name':
          fieldValue = category.name;
          break;
        case 'Asset Account':
          fieldValue = category.assetAccount;
          break;
        case 'Expense Account':
          fieldValue = category.expenseAccount;
          break;
        case 'Income Account':
          fieldValue = category.incomeAccount;
          break;
        default:
          fieldValue = category.name;
      }
      
      return fieldValue.toLowerCase().includes(searchForm.searchValue.toLowerCase());
    });
    setFinanceCategories(filtered);
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setFinanceCategories(mockFinanceCategories);
    setSelectedFinanceCategory(null);
  };

  const handleRowClick = (category: FinanceCategoryData) => {
    setSelectedFinanceCategory(category);
  };

  const handleRowDoubleClick = (category: FinanceCategoryData) => {
    if (onSelect) {
      onSelect(category);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedFinanceCategory(null);
    onClose();
  };


  const handleClears = () => {
    setSelectedFinanceCategory(null);
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedFinanceCategory?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Asset Account',
      accessorKey: 'assetAccount',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedFinanceCategory?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Expense Account',
      accessorKey: 'expenseAccount',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedFinanceCategory?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Income Account',
      accessorKey: 'incomeAccount',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedFinanceCategory?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    }
  ], [selectedFinanceCategory]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>Finance Category</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => {}}
              onKeyDown={() => {}}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Form>
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className="me-3">
                  <button
                    onClick={handleSearch}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1}>
                  <button
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Finance Category Table */}
              <Row>
                <Col xs={12}>
                  <TableContainerReactTable
                    columns={columns}
                    data={financeCategories || []}
                    isGlobalFilter={false}
                    customPageSize={10}
                    divClass="mb-0"
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="table-light"
                    thClass="px-3 py-2"
                    verticalLines={true}
                  />
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <button
          onClick={handleClears}
          className="btn btn-soft-warning btn-sm"
          style={{ minWidth: 80 }}
        >
          Clear
        </button>
        <button
          className="btn btn-soft-dark btn-sm"
          onClick={handleCancel}
          style={{ minWidth: 80 }}
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default FinanceCategoryModal;
